import React, { useEffect, useMemo, useState } from 'react';
import { DataProvider, GlobalActionsProvider } from '@plasmicapp/host';
import { hasDoctorIdentity } from '@/common/utils/doctorIdentity';
import { useDoctorDeviceCached } from '@/common/hooks/useDoctorDeviceCached';
import { getCookie } from 'cookies-next';
import getConfig from 'next/config';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { growthbook } from '@/common/growthbook/client';

const { publicRuntimeConfig } = getConfig();
const isEnabledGrowthbook =
  !!publicRuntimeConfig.GROWTHBOOK_API_HOST &&
  !!publicRuntimeConfig.GROWTHBOOK_CLIENT_KEY;

interface GrowthbookGlobalContextProps {
  previewAttributes?: Record<string, string>;
  apiHost: string;
  clientKey: string;
}

export const GrowthbookGlobalContext = ({
  children,
  previewAttributes,
}: React.PropsWithChildren<GrowthbookGlobalContextProps>) => {
  const [isReady, setIsReady] = useState(growthbook.ready);
  const [featuresVersion, setFeaturesVersion] = useState(0);
  const [attr, setAttr] = useState({});
  const user = useUserInfoStore(state => state.info);
  const isDoctor = hasDoctorIdentity(user, useDoctorDeviceCached());

  const getDoctorSpecialties = useMemo(() => {
    if (!isDoctor) return [];

    const expertises =
      user?.provider && Array.isArray((user.provider as any).expertises)
        ? (user.provider as any).expertises.map((item: any) => item?.expertise?.id).filter((id: any) => id != null)
        : [];

    return expertises;
  }, [isDoctor, user]);

  const syncAttributes = (extra: Record<string, unknown> = {}) => {
    const currentAttributes = growthbook.getAttributes();
    growthbook.setAttributes({
      ...currentAttributes,
      ...attr,
      ...previewAttributes,
      ...extra,
      url: window.location.href,
      id: getCookie('terminal_id'),
      user_id: user?.id,
      is_doctor: isDoctor,
      slug: user?.provider?.slug,
      host: window.location.host,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      doctor_specialties: getDoctorSpecialties,
    });
  };

  useEffect(() => {
    syncAttributes();
    if (isEnabledGrowthbook && growthbook.ready) {
      growthbook.refreshFeatures?.();
    }
    setFeaturesVersion(version => version + 1);
  }, [user, isDoctor, getDoctorSpecialties, previewAttributes, attr]);

  useEffect(() => {
    if (!isEnabledGrowthbook) return;

    setIsReady(growthbook.ready);

    const unsubscribe = growthbook.subscribe(() => {
      setIsReady(growthbook.ready);
      setFeaturesVersion(version => version + 1);
    });

    if (!growthbook.ready) {
      growthbook.loadFeatures({ autoRefresh: true });
    }

    return () => unsubscribe?.();
  }, []);

  const actions = useMemo(
    () => ({
      setAttributes: (attributes: Record<string, any>) => {
        setAttr(attributes);
        syncAttributes(attributes);
        if (isEnabledGrowthbook) growthbook.refreshFeatures?.();
        setFeaturesVersion(version => version + 1);
      },
      setAttributeOverrides: (attributes: Record<string, any>) => {
        setAttr(prev => ({ ...prev, ...attributes }));
        syncAttributes(attributes);
        if (isEnabledGrowthbook) growthbook.refreshFeatures?.();
        setFeaturesVersion(version => version + 1);
      },
    }),
    [user, isDoctor, getDoctorSpecialties, previewAttributes, attr],
  );

  const features = useMemo(() => {
    if (!growthbook.ready) return undefined;

    // Plain loop, not reduce+spread: spreading the accumulator per feature was O(n²) and showed
    // up as the hottest JS function of a search → search transition (docs/performance.md §۳.۷).
    // Same isOn/getFeatureValue calls in the same order — they can fire experiment tracking.
    const all = growthbook.getFeatures();
    const result: Record<string, unknown> = {};
    for (const name of Object.keys(all)) {
      const isBoolean = typeof all[name]?.defaultValue === 'boolean';
      result[name] = isBoolean ? growthbook.isOn(name) : growthbook.getFeatureValue(name, undefined);
    }
    return result;
  }, [isReady, featuresVersion, user?.id, isDoctor]);

  return (
    <GlobalActionsProvider contextName="GrowthbookGlobalContext" actions={actions}>
      <DataProvider name="Growthbook" data={{ features, isReady, attributes: growthbook.getAttributes() }}>
        {children}
      </DataProvider>
    </GlobalActionsProvider>
  );
};
